import { useEffect, useMemo, useState } from "react";
import { api, extractError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, RotateCcw, Trash2, Search, Bug } from "lucide-react";

const FR_DATE = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

export default function ErrorsPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [openCount, setOpenCount] = useState(0);
  const [filter, setFilter] = useState("open");   // "open" | "resolved" | "all"
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    try {
      const params = {};
      if (filter === "open") params.resolved = false;
      if (filter === "resolved") params.resolved = true;
      if (q.trim()) params.page = q.trim();
      const { data } = await api.get("/errors", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setOpenCount(data.open || 0);
    } catch (e) { toast.error(extractError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, q]);

  const resolve = async (id) => {
    try { await api.patch(`/errors/${id}/resolve`); toast.success("Marqué comme résolu"); load(); }
    catch (e) { toast.error(extractError(e)); }
  };
  const reopen = async (id) => {
    try { await api.patch(`/errors/${id}/reopen`); toast.success("Ré-ouvert"); load(); }
    catch (e) { toast.error(extractError(e)); }
  };
  const del = async (id) => {
    if (!confirm("Supprimer ce rapport ?")) return;
    try { await api.delete(`/errors/${id}`); toast.success("Supprimé"); load(); }
    catch (e) { toast.error(extractError(e)); }
  };
  const clearResolved = async () => {
    if (!confirm("Supprimer tous les rapports résolus ?")) return;
    try { const { data } = await api.delete("/errors", { params: { only_resolved: true } }); toast.success(`${data.deleted} rapport(s) supprimé(s)`); load(); }
    catch (e) { toast.error(extractError(e)); }
  };

  // Regrouper par message pour repérer les récurrences
  const groupedTop = useMemo(() => {
    const map = new Map();
    items.forEach(r => {
      const k = `${r.page}::${(r.message || "").slice(0, 100)}`;
      const cur = map.get(k) || { key: k, page: r.page, message: r.message, count: 0, last: r.created_at };
      cur.count += 1;
      if (r.created_at > cur.last) cur.last = r.created_at;
      map.set(k, cur);
    });
    return Array.from(map.values()).sort((a, b) => b.count - a.count).slice(0, 5);
  }, [items]);

  const detail = openId ? items.find(r => r.id === openId) : null;

  return (
    <div className="space-y-6" data-testid="errors-page">
      <header>
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Admin</div>
        <h1 className="font-brand text-3xl md:text-4xl leading-tight flex items-center gap-2">
          <Bug className="h-7 w-7 text-primary" /> Journal des erreurs
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Toutes les erreurs de rendu React capturées automatiquement par l'ErrorBoundary.</p>
      </header>

      {/* KPIs + top récurrences */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="border-border/60"><CardContent className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Ouverts</div>
          <div className="font-brand text-3xl text-destructive tabular-nums mt-1" data-testid="stat-open">{openCount}</div>
        </CardContent></Card>
        <Card className="border-border/60"><CardContent className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Total historique</div>
          <div className="font-brand text-3xl tabular-nums mt-1" data-testid="stat-total">{total}</div>
        </CardContent></Card>
        <Card className="border-border/60"><CardContent className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Top récurrences (vue courante)</div>
          <div className="mt-1 space-y-0.5 text-xs">
            {groupedTop.length === 0
              ? <div className="text-muted-foreground italic">Aucun</div>
              : groupedTop.map((g) => (
                  <div key={g.key} className="flex items-center gap-2">
                    <Badge className="rounded-full bg-primary/15 text-primary border-primary/40 text-[10px]">×{g.count}</Badge>
                    <span className="truncate" title={`${g.page} — ${g.message}`}>
                      <code className="text-primary">{g.page}</code> — <span className="text-muted-foreground">{(g.message || "").slice(0, 60)}</span>
                    </span>
                  </div>
                ))}
          </div>
        </CardContent></Card>
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1 rounded-full bg-muted p-1">
          {[
            { v: "open", label: `Ouverts (${openCount})` },
            { v: "resolved", label: "Résolus" },
            { v: "all", label: "Tous" },
          ].map(t => (
            <button
              key={t.v}
              onClick={() => setFilter(t.v)}
              className={`px-3 h-7 rounded-full text-xs font-semibold ${filter === t.v ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}
              data-testid={`errors-filter-${t.v}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Filtrer par page (ex: /materiel)…" className="pl-9" data-testid="errors-search" />
        </div>
        <Button
          variant="outline"
          size="sm"
          className="rounded-full border-destructive/40 text-destructive hover:bg-destructive/10 ml-auto"
          onClick={clearResolved}
          data-testid="errors-clear-resolved"
        >
          <Trash2 className="h-3.5 w-3.5 mr-1" /> Purger les résolus
        </Button>
      </div>

      {/* Table */}
      <Card className="border-border/60"><CardContent className="p-0">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
              <th className="text-left px-4 py-3">Date</th>
              <th className="text-left px-4 py-3">Page</th>
              <th className="text-left px-4 py-3">Message</th>
              <th className="text-left px-4 py-3">Utilisateur</th>
              <th className="text-left px-4 py-3">Statut</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={6} className="text-center py-10 text-muted-foreground italic">Aucune erreur — tout roule 🎉</td></tr>
            )}
            {items.map(r => (
              <tr key={r.id} className="border-b border-border/60 hover:bg-muted cursor-pointer" onClick={() => setOpenId(r.id)} data-testid={`error-row-${r.id}`}>
                <td className="px-4 py-2 tabular-nums text-xs text-muted-foreground">{FR_DATE(r.created_at)}</td>
                <td className="px-4 py-2 max-w-[220px] truncate"><code className="text-primary text-xs">{r.page}</code></td>
                <td className="px-4 py-2 max-w-[380px] truncate text-xs">{r.message}</td>
                <td className="px-4 py-2 text-xs">
                  {r.user_name ? <>{r.user_name} <span className="text-muted-foreground">({r.user_role})</span></> : <span className="text-muted-foreground italic">Anonyme</span>}
                </td>
                <td className="px-4 py-2">
                  {r.resolved
                    ? <Badge className="rounded-full bg-emerald-500/15 text-emerald-300 border-emerald-500/50 text-[10px]"><CheckCircle2 className="h-3 w-3 mr-1 inline" /> Résolu</Badge>
                    : <Badge className="rounded-full bg-destructive/15 text-destructive border-destructive/40 text-[10px]"><AlertTriangle className="h-3 w-3 mr-1 inline" /> Ouvert</Badge>}
                </td>
                <td className="px-4 py-2 text-right whitespace-nowrap" onClick={e => e.stopPropagation()}>
                  {r.resolved ? (
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]" onClick={() => reopen(r.id)} data-testid={`error-reopen-${r.id}`}>
                      <RotateCcw className="h-3 w-3 mr-1" /> Ré-ouvrir
                    </Button>
                  ) : (
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px] text-emerald-300 hover:bg-emerald-500/10" onClick={() => resolve(r.id)} data-testid={`error-resolve-${r.id}`}>
                      <CheckCircle2 className="h-3 w-3 mr-1" /> Résolu
                    </Button>
                  )}
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:bg-destructive/10" onClick={() => del(r.id)} data-testid={`error-delete-${r.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent></Card>

      {/* Détail overlay */}
      {detail && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpenId(null)}>
          <Card className="max-w-3xl w-full max-h-[90vh] overflow-y-auto border-border/60" onClick={e => e.stopPropagation()} data-testid="error-detail-dialog">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{FR_DATE(detail.created_at)}</div>
                  <h2 className="font-brand text-xl mt-1">Détail du rapport</h2>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setOpenId(null)}>Fermer</Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                <div><div className="text-muted-foreground font-bold text-[10px] uppercase">Page</div><code className="text-primary break-all">{detail.page}</code></div>
                <div><div className="text-muted-foreground font-bold text-[10px] uppercase">Utilisateur</div><div>{detail.user_name || "Anonyme"} {detail.user_email && <span className="text-muted-foreground">— {detail.user_email}</span>}</div></div>
                <div className="md:col-span-2"><div className="text-muted-foreground font-bold text-[10px] uppercase">Navigateur</div><div className="text-muted-foreground break-all">{detail.user_agent}</div></div>
              </div>
              <div>
                <div className="text-muted-foreground font-bold text-[10px] uppercase mb-1">Message</div>
                <pre className="bg-muted border border-border/60 rounded-lg p-3 text-xs whitespace-pre-wrap break-words">{detail.message}</pre>
              </div>
              {detail.stack && (
                <div>
                  <div className="text-muted-foreground font-bold text-[10px] uppercase mb-1">Stack</div>
                  <pre className="bg-muted border border-border/60 rounded-lg p-3 text-[11px] whitespace-pre-wrap break-words max-h-64 overflow-auto">{detail.stack}</pre>
                </div>
              )}
              {detail.component_stack && (
                <div>
                  <div className="text-muted-foreground font-bold text-[10px] uppercase mb-1">Composant React</div>
                  <pre className="bg-muted border border-border/60 rounded-lg p-3 text-[11px] whitespace-pre-wrap break-words max-h-48 overflow-auto">{detail.component_stack}</pre>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                {detail.resolved ? (
                  <Button variant="outline" className="rounded-full" onClick={() => { reopen(detail.id); setOpenId(null); }}>
                    <RotateCcw className="h-4 w-4 mr-2" /> Ré-ouvrir
                  </Button>
                ) : (
                  <Button className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => { resolve(detail.id); setOpenId(null); }}>
                    <CheckCircle2 className="h-4 w-4 mr-2" /> Marquer résolu
                  </Button>
                )}
                <Button variant="outline" className="rounded-full border-destructive/40 text-destructive" onClick={() => { del(detail.id); setOpenId(null); }}>
                  <Trash2 className="h-4 w-4 mr-2" /> Supprimer
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
